import { DeductionDocument } from "@/core/entities/deduction";
import { Button, Chip, Flex, Icon, Icons, Skeleton, Span, TableColumnType, Tooltip } from "@ir/ui";
import { ReactNode } from "react";

const MAX_FILE_NAME_LENGTH = 45;

const DocumentSkeleton = (width: number) => <Skeleton height={9} width={width} animation="wave" variant="rounded" className="mt-1 mb-1" />;

const getFileNameDisplay = (fileName: string) => {
  const fileExtension = fileName?.split(".").pop();
  const fileNameWithoutExtension = fileName?.split(".").slice(0, -1).join(".");
  return fileName?.length > MAX_FILE_NAME_LENGTH ? fileNameWithoutExtension?.slice(0, MAX_FILE_NAME_LENGTH) + "..." + fileExtension : fileName;
};
interface DataType {
  operation?: ReactNode;
}

interface DeductionDocumentsTableProps {
  isDeductionsLoading: boolean;
  workspaceSettings: any;
  downloadingDocuments: number[];
  deletingDocuments: number[];
  handleDocumentDownload: (deductionDocument: DeductionDocument) => void | Promise<void>;
  handleDocumentDelete: (deductionDocumentId: number) => void | Promise<void>;
  hasDeletePermission: boolean;
}

const DeductionDocumentsTableColumns = ({
  isDeductionsLoading,
  workspaceSettings,
  downloadingDocuments,
  deletingDocuments,
  handleDocumentDownload,
  handleDocumentDelete,
  hasDeletePermission,
}: DeductionDocumentsTableProps): TableColumnType<DeductionDocument & DataType>[] => {
  const columns: TableColumnType<DeductionDocument & DataType>[] = [
    {
      title: "File Name",
      width: 280,
      dataIndex: "fileDisplayName" as keyof DeductionDocument,
      key: "name",
      align: "left",
      cellRender: (value, record) => {
        if (!isDeductionsLoading) {
          const fileName = value as string;
          const fileNameDisplay =
            fileName?.length > MAX_FILE_NAME_LENGTH ? (
              <Tooltip content={fileName} placement="top start" className="file-name-tooltip">
                <Button
                  noStyle
                  className="text-secondary-600 font-medium"
                  style={{ whiteSpace: "break-spaces", textAlign: "left" }}
                  onPress={() => handleDocumentDownload(record)}
                  isDisabled={downloadingDocuments.includes(record.id)}
                >
                  {getFileNameDisplay(fileName)}
                </Button>
              </Tooltip>
            ) : (
              <Button
                noStyle
                className="text-secondary-600 font-medium"
                onPress={() => handleDocumentDownload(record)}
                isDisabled={downloadingDocuments.includes(record.id)}
              >
                {fileName}{" "}
              </Button>
            );
          return (
            <Flex align="center" gap={0.75} wrap="no-wrap">
              <Span className="flex">
                <Icon
                  size="small"
                  name={downloadingDocuments.includes(record.id) ? Icons.Loading : Icons.File}
                  color="grey"
                  className={downloadingDocuments.includes(record.id) ? "icon-fast-loading" : ""}
                />
              </Span>
              <Span>{fileNameDisplay}</Span>
            </Flex>
          );
        }
        return DocumentSkeleton(150);
      },
    },
    {
      title: "Date Uploaded",
      width: 120,
      dataIndex: "createdAt" as keyof DeductionDocument,
      key: "date",
      align: "left",
      cellRender: (value, record) => {
        if (!isDeductionsLoading) {
          return workspaceSettings?.displayDateFormat ? record.createdAtFormatted(workspaceSettings.displayDateFormat) : value;
        }
        return DocumentSkeleton(150);
      },
    },
    {
      title: "Tags",
      width: 150,
      dataIndex: "documentType" as keyof DeductionDocument,
      key: "tags",
      align: "left",
      cellRender: value =>
        isDeductionsLoading ? (
          DocumentSkeleton(100)
        ) : (
          <Chip size="small" color="grey" label={value} className="chip-document-type bg-grey-50-transparent border-color-grey-200 text-grey-700" />
        ),
    },
    {
      title: "",
      width: 30,
      dataIndex: "operation" as keyof DataType,
      key: "operation",
      align: "right",
      cellRender: (_, record) => (
        <Flex justifyContent="end">
          {isDeductionsLoading ? (
            DocumentSkeleton(22)
          ) : (
            <Tooltip content="Delete document" placement="top start">
              <Button
                noStyle
                size="small"
                onPress={() => handleDocumentDelete(record.id)}
                isDisabled={deletingDocuments.length != 0 || !record.deletable} // one document is being deleted at a time
              >
                {record.deletable && (
                  <Icon
                    slot="end"
                    color="grey"
                    className={deletingDocuments.includes(record.id) ? "icon-fast-loading" : ""}
                    name={deletingDocuments.includes(record.id) ? Icons.Loading : Icons.Close}
                  />
                )}
              </Button>
            </Tooltip>
          )}
        </Flex>
      ),
    },
  ];
  return hasDeletePermission ? columns : columns.filter(column => column.dataIndex !== "operation");
};

export default DeductionDocumentsTableColumns;
